import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const [activeSlug, setActiveSlug] = useState('all');
  const [filteredComics, setFilteredComics] = useState(comics);

  useEffect(() => {
    const pathParts = window.location.pathname.split('/');
    const slugFromPath = pathParts[pathParts.length - 1];
    if (slugFromPath && slugFromPath !== 'category' && categories.some(c => c.slug === slugFromPath)) {
      setActiveSlug(slugFromPath);
    } else {
      setActiveSlug('all');
    }
  }, []);

  const handleCategoryChange = useCallback((slug: string) => {
    setActiveSlug(slug);
    if (slug === 'all') {
      setFilteredComics(comics);
    } else {
      setFilteredComics(comics.filter(c => c.categorySlug === slug));
    }
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  useEffect(() => {
    handleCategoryChange(activeSlug);
  }, [activeSlug, handleCategoryChange]);

  const currentCategoryName = categories.find(c => c.slug === activeSlug)?.name || '全部';

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen bg-background-50">
        <section className="pt-24 pb-8 md:pt-28 md:pb-12 bg-background-100">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{currentCategoryName}</span>
            </nav>

            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-2">
              {currentCategoryName === '全部' ? '韩国漫画大全' : `${currentCategoryName}漫画`}
            </h1>
            <p className="text-sm text-foreground-500">
              发现精彩韩国漫画，享受免费在线阅读体验
            </p>
          </div>
        </section>

        <section className="py-8 md:py-12">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 flex-wrap mb-8">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {filteredComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="group"
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-100">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 right-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        comic.status === '连载中'
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-secondary-500 text-background-50'
                      }`}>
                        {comic.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {comic.title}
                  </h3>
                  <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                    <span>{comic.author}</span>
                    <span>·</span>
                    <span className="flex items-center gap-0.5">
                      <i className="ri-star-fill text-accent-400 text-xs"></i>
                      {comic.rating}
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {filteredComics.length === 0 && (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-book-2-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">该分类暂无漫画内容，敬请期待</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}